import { useMemo, useState } from 'react';
import { goBack, navigate } from '../app/router';
import { Icon } from '../components/ui/Icon';
import { Group, IconButton, Row, ScreenHeader, Switch } from '../components/ui/ui';
import { formatDateTime } from '../lib/format';
import { displayTitle } from '../notes/model';
import { TEMPLATES } from '../notes/templates';
import { isIOS, isStandalone, notificationsSupported, SystemNotificationAdapter, type PermissionState } from '../notifications/adapter';
import { store, useStore } from '../state/store';
import type { Schedule } from '../storage/schemas';
import { describeRule, nextOccurrence } from './recurrence';
import './schedules.css';

const ROUTINES: { label: string; time: string; weekdays: number[]; kind: 'daily' | 'weekly'; template: string }[] = [
  { label: 'Morning note · 7:00', time: '07:00', weekdays: [0, 1, 2, 3, 4, 5, 6], kind: 'daily', template: 'morning' },
  { label: 'Work note · 9:00 weekdays', time: '09:00', weekdays: [1, 2, 3, 4, 5], kind: 'weekly', template: 'work' },
  { label: 'Study note · 18:00', time: '18:00', weekdays: [1, 2, 3, 4], kind: 'weekly', template: 'study' },
  { label: 'Shopping list · Saturday', time: '10:00', weekdays: [6], kind: 'weekly', template: 'shopping' },
  { label: 'Evening routine · 21:00', time: '21:00', weekdays: [0, 1, 2, 3, 4, 5, 6], kind: 'daily', template: 'evening' },
];

export function NotificationPanel() {
  const optIn = useStore((s) => s.settings.notificationsOptIn);
  const [perm, setPerm] = useState<PermissionState>(() => new SystemNotificationAdapter().permission());
  const ios = isIOS();
  const standalone = isStandalone();
  let status: string;
  if (!notificationsSupported()) status = ios && !standalone ? 'On iPhone and iPad, add MemoNest to your Home Screen first (Share → Add to Home Screen), then open it from there.' : 'This browser cannot show system notifications. Reminders appear inside MemoNest instead.';
  else if (perm === 'granted') status = 'System notifications are on. They appear while MemoNest is open or was used recently; your device may delay them.';
  else if (perm === 'denied') status = 'Notifications are blocked for this site. Reminders appear inside MemoNest. You can allow them again in your browser’s site settings.';
  else status = 'MemoNest asks only when you tap the button. Nothing is sent to a server.';
  return (
    <Group title="Notifications" footer={status}>
      <Row label="System notifications" detail={perm === 'unsupported' ? 'Not available here' : perm === 'granted' ? 'Allowed' : perm === 'denied' ? 'Blocked' : 'Not asked yet'}>
        {perm === 'default' && (
          <button
            type="button"
            className="btn is-small is-primary"
            onClick={async () => {
              const p = await new SystemNotificationAdapter().requestPermission();
              setPerm(p);
              store.updateSettings({ notificationsOptIn: p === 'granted' });
            }}
          >
            Enable
          </button>
        )}
        {perm === 'granted' && <Switch label="Use system notifications" checked={optIn} onChange={(v) => store.updateSettings({ notificationsOptIn: v })} />}
      </Row>
    </Group>
  );
}

function ScheduleRow({ s }: { s: Schedule }) {
  const note = useStore((st) => st.notes[s.noteId]);
  const next = s.enabled ? nextOccurrence(s.rule, Date.now()) : null;
  return (
    <div className={`sch-row ${s.enabled ? '' : 'is-off'}`}>
      <button type="button" className="sch-open" onClick={() => navigate({ name: 'schedule', id: s.id })}>
        <span className="sch-title">{s.title || (note ? displayTitle(note) : 'Missing note')}</span>
        <span className="sch-desc">{describeRule(s.rule)}</span>
        <span className="sch-badges">
          {s.notify && (
            <span className="sch-badge">
              <Icon name="bell" size={13} /> Reminder{s.leadMinutes ? ` −${s.leadMinutes}m` : ''}
            </span>
          )}
          {s.switchNote && (
            <span className="sch-badge">
              <Icon name="phone" size={13} /> Switches note
            </span>
          )}
          <span className="sch-next-label">{s.enabled ? (next ? `Next: ${formatDateTime(next, s.rule.timeZone)}` : 'Finished') : 'Off'}</span>
        </span>
      </button>
      <Switch label={`${s.enabled ? 'Disable' : 'Enable'} schedule`} checked={s.enabled} onChange={(v) => void store.saveSchedule({ ...s, enabled: v, updatedAt: Date.now() })} />
    </div>
  );
}

export function SchedulesScreen({ noteId }: { noteId?: string }) {
  const schedules = useStore((s) => s.schedules);
  const notes = useStore((s) => s.notes);
  const activeId = useStore((s) => s.active.noteId);
  const list = useMemo(() => schedules.filter((s) => !noteId || s.noteId === noteId).sort((a, b) => (nextOccurrence(a.rule, Date.now()) ?? Infinity) - (nextOccurrence(b.rule, Date.now()) ?? Infinity)), [schedules, noteId]);
  const scopeNote = noteId ? notes[noteId] : undefined;

  const newSchedule = (preset?: (typeof ROUTINES)[number]) => {
    let target = noteId ?? activeId ?? undefined;
    if (preset && !noteId) {
      const t = TEMPLATES.find((x) => x.id === preset.template)!;
      const n = store.createNote({ designId: t.designId, title: t.name, lines: t.lines.map((l, i) => ({ id: `l${Date.now().toString(36)}${i}`, text: l.text, kind: l.kind, checked: false, marks: l.heading ? { heading: true } : undefined })) });
      target = n.id;
    }
    if (preset) {
      sessionStorage.setItem(
        'memonest.schedulePreset',
        JSON.stringify({ noteId: target, title: preset.label.split(' · ')[0], switchNote: true, notify: true, rule: { kind: preset.kind, time: preset.time, weekdays: preset.weekdays } }),
      );
    }
    navigate({ name: 'schedule', id: `new:${target ?? ''}` });
  };

  return (
    <div className="screen schedules">
      <ScreenHeader title={scopeNote ? 'Note schedules' : 'Schedules'} onBack={() => goBack()} actions={<IconButton icon="plus" label="New schedule" onClick={() => newSchedule()} />} />
      {scopeNote && <p className="sch-scope">For “{displayTitle(scopeNote)}”.</p>}
      <Group title={list.length ? 'Your schedules' : undefined}>
        {list.length === 0 ? (
          <Row label="No schedules yet" detail="Choose when a note shows up and when you get reminded." />
        ) : (
          list.map((s) => <ScheduleRow key={s.id} s={s} />)
        )}
      </Group>
      <button type="button" className="btn is-primary sch-new" onClick={() => newSchedule()}>
        New schedule
      </button>
      <Group title="Quick routines" footer={noteId ? 'Creates a schedule for this note with the routine’s time.' : 'Creates a note from a template and schedules it. You can change everything afterwards.'}>
        {ROUTINES.map((r) => (
          <Row key={r.label} label={r.label} icon="alarm" onClick={() => newSchedule(r)} />
        ))}
      </Group>
      <Group>
        <Row label="Upcoming timeline" icon="timeline" onClick={() => navigate({ name: 'upcoming' })} />
      </Group>
      <NotificationPanel />
    </div>
  );
}
